import { Close, Pause, PlayArrow } from "@mui/icons-material";
import { Box, IconButton, Slider, Stack, Typography, useTheme } from "@mui/material";
import { useContext, useEffect, useState } from "react";

import { AudioAppContext } from "@/models/AudioAppContextProvider";
import { FeedContext } from "@/models/FeedContextProvider";
import { UxContext } from "@/models/UxStateProvider";
import { formatMessageTimestamp } from "@/utils/dateFormat";

export default function GlobalAudioPlayer() {
  const theme = useTheme();
  const { activeItemId, playerPlaying, playItem, pauseItem, audioElement, closePlayer } = useContext(AudioAppContext);
  const { feedMap } = useContext(FeedContext);

  const [position, setPosition] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Find the currently playing item from the feed
  const activeItem = feedMap?.find((item) => item.id === activeItemId);

  // Get sender name and date
  const senderName = activeItem?.account?.name || "Unknown";
  const itemDate = activeItem?.createdAt ? formatMessageTimestamp(new Date(activeItem.createdAt)) : "";

  // Format time as mm:ss
  const formatTime = (seconds: number) => {
    if (!seconds || !isFinite(seconds)) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Update position and time from audio element
  useEffect(() => {
    if (!audioElement || !activeItemId) return;

    const handleTimeUpdate = () => {
      if (audioElement.duration) {
        setCurrentTime(audioElement.currentTime);
        setDuration(audioElement.duration);
        setPosition((audioElement.currentTime / audioElement.duration) * 100);
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(audioElement.duration);
    };

    const handleEnded = () => {
      setPosition(0);
      setCurrentTime(0);
    };

    audioElement.addEventListener("timeupdate", handleTimeUpdate);
    audioElement.addEventListener("loadedmetadata", handleLoadedMetadata);
    audioElement.addEventListener("ended", handleEnded);

    return () => {
      audioElement.removeEventListener("timeupdate", handleTimeUpdate);
      audioElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audioElement.removeEventListener("ended", handleEnded);
    };
  }, [audioElement, activeItemId]);

  // Reset when no active item
  useEffect(() => {
    if (!activeItemId) {
      setPosition(0);
      setCurrentTime(0);
      setDuration(0);
    }
  }, [activeItemId]);

  // Handle seek
  const handleSeek = (_event: Event, newValue: number | number[]) => {
    if (!audioElement || !duration) return;
    const seekPosition = (newValue as number) / 100;
    audioElement.currentTime = seekPosition * duration;
    setPosition(newValue as number);
  };

  // Handle play/pause
  const handlePlayPause = () => {
    if (playerPlaying) {
      pauseItem();
    } else if (activeItemId) {
      playItem();
    }
  };
  const { setFeedViewCollapsed } = useContext(UxContext);

  const closeAndHidePlayer = () => {
    closePlayer();
    setFeedViewCollapsed(() => false);
  };

  if (!activeItemId) {
    return <></>;
  }

  return (
    <Box
      sx={{
        height: "100%",
        position: "relative",
        background: theme.palette.secondary.dark,
        px: 1,
        py: 1,
        zIndex: 100,
        borderRadius: 1,
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={() => closeAndHidePlayer()}
        size="small"
        sx={{
          position: "absolute",
          top: 2,
          right: 2,
          color: theme.palette.text.secondary,
          "&:hover": { color: theme.palette.text.primary },
          width: 20,
          height: 20,
        }}
      >
        <Close sx={{ fontSize: 14 }} />
      </IconButton>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        {/* Play/Pause Button */}
        <IconButton
          onClick={handlePlayPause}
          size="small"
          sx={{
            background: theme.palette.brand.primary.main,
            color: theme.palette.primary.main,
            "&:hover": {
              background: theme.palette.brand.primary.light,
            },
            width: 36,
            height: 36,
          }}
        >
          {playerPlaying ? <Pause /> : <PlayArrow />}
        </IconButton>

        {/* Title - Sender and Date */}
        <Box sx={{ minWidth: 150, maxWidth: 200 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: "12px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {senderName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: "10px",
              color: theme.palette.text.secondary,
              whiteSpace: "nowrap",
            }}
          >
            {itemDate}
          </Typography>
        </Box>

        {/* Current Time */}
        <Typography variant="caption" sx={{ fontSize: "11px", minWidth: 40 }}>
          {formatTime(currentTime)}
        </Typography>

        {/* Seek Bar */}
        <Slider
          value={position}
          onChange={handleSeek}
          size="small"
          sx={{
            flexGrow: 1,
            color: theme.palette.brand.primary.main,
            "& .MuiSlider-thumb": {
              width: 12,
              height: 12,
            },
            "& .MuiSlider-track": {
              height: 4,
            },
            "& .MuiSlider-rail": {
              height: 4,
              opacity: 0.3,
            },
          }}
        />

        {/* Duration */}
        <Typography variant="caption" sx={{ fontSize: "11px", minWidth: 40 }}>
          {formatTime(duration)}
        </Typography>
      </Stack>
    </Box>
  );
}
